import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useLocateLogic, useReceiveReturned } from '../../hooks';
import { Breadcrumb } from '../display';
import {
  LocatedProductsSection,
  LocateFinishedMessage,
  ProductsToLocateHeader,
  ProductsToLocateSection,
  ScanLocationSection,
} from '../display/locate';

export const DetailContainer: React.FC = () => {
  const { t } = useTranslation('locationordersDetailModule');
  const { id } = useParams<{ id: string }>();

  const returnedId = id ? parseInt(id, 10) : 0;
  const { returnedData, isLoading } = useReceiveReturned(returnedId);

  const processedProducts = returnedData?.lines || [];

  // Por id de línea: varias filas del mismo producto (distintos lotes) no pisan la cantidad
  const processedProductsQuantities: Record<string, number> = {};
  processedProducts.forEach((p: any) => {
    if (p.id != null) processedProductsQuantities[String(p.id)] = p.quantity;
  });

  const processedProductsTotals: Record<string, number> = {};
  processedProducts.forEach((p: any) => {
    if (p.id != null) processedProductsTotals[String(p.id)] = p.totalQuantity;
  });

  const {
    productsToLocate,
    locatedProducts,
    toLocateSortBy,
    locatedSortBy,
    currentLocation,
    isValidatingLocation,
    productBeingLocated,
    quantityToLocate,
    setQuantityToLocate,
    handleLocationScan,
    handleScanUnits,
    handleBatchLineSelect,
    handleProductLocate,
    handleClearLocation,
    handleToLocateSort,
    handleLocatedSort,
    isLocating,
    batchSelectionCandidates,
    selectedBatchLineId,
  } = useLocateLogic({
    returnedId,
    processedProducts,
    processedProductsQuantities,
    processedProductsTotals,
  });

  const [toLocateSearch, setToLocateSearch] = useState('');
  const [locatedSearch, setLocatedSearch] = useState('');

  const filteredProductsToLocate = toLocateSearch.trim()
    ? productsToLocate.filter(
        (p: any) =>
          (p.name || '').toLowerCase().includes(toLocateSearch.toLowerCase()) ||
          (p.code || '').toLowerCase().includes(toLocateSearch.toLowerCase()),
      )
    : productsToLocate;

  const filteredLocatedProducts = locatedSearch.trim()
    ? locatedProducts.filter(
        (p: any) =>
          (p.name || '').toLowerCase().includes(locatedSearch.toLowerCase()) ||
          (p.code || '').toLowerCase().includes(locatedSearch.toLowerCase()),
      )
    : locatedProducts;

  if (!id) {
    return (
      <div className="container mx-auto p-4 max-w-6xl">
        <div className="text-center py-8">
          <p className="text-muted-foreground">ID no válido</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('loading.default', { defaultValue: 'Cargando...' })}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!returnedData) {
    return (
      <div className="container mx-auto p-4 max-w-6xl">
        <div className="text-center py-8">
          <p className="text-muted-foreground">No se encontró la orden de ubicación</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-4 max-w-6xl h-[calc(100vh-6rem)] flex flex-col">
        <div className="mb-6">
          <Breadcrumb
            returnedId={returnedId}
            code={returnedData.code || ''}
            status={returnedData.status || 'pending'}
          />
        </div>

        {/* Mobile layout */}
        <div className={`flex flex-col lg:hidden flex-1 min-h-0 ${locatedProducts.length > 0 ? 'pb-20' : ''}`}>
          <div className="flex-shrink-0 mb-4">
            {productsToLocate.length === 0 && locatedProducts.length > 0 ? (
              <LocateFinishedMessage />
            ) : (
              <ScanLocationSection
                onLocationScan={handleLocationScan}
                onScanUnits={handleScanUnits}
                onClearLocation={handleClearLocation}
                currentLocation={currentLocation}
                isValidating={isValidatingLocation}
                productBeingLocated={productBeingLocated}
                quantityToLocate={quantityToLocate}
                onQuantityChange={setQuantityToLocate}
                isLocating={isLocating}
                cartCode={returnedData?.cart_code}
                batchSelectionCandidates={batchSelectionCandidates}
                selectedBatchLineId={selectedBatchLineId}
                onBatchLineSelect={handleBatchLineSelect}
                onConfirmLocate={(batchId) => {
                  if (productBeingLocated && currentLocation) {
                    handleProductLocate(productBeingLocated.id, currentLocation.id, productBeingLocated.code, batchId);
                  }
                }}
              />
            )}
          </div>

          <div className="flex-1 min-h-0">
            <div className="bg-background border border-border rounded-lg p-4 h-full flex flex-col">
              <ProductsToLocateHeader
                products={filteredProductsToLocate}
                currentSort={toLocateSortBy}
                onSort={handleToLocateSort}
                searchQuery={toLocateSearch}
                onSearchChange={setToLocateSearch}
              />
              <div className="flex-1 overflow-y-auto">
                <ProductsToLocateSection
                  products={filteredProductsToLocate}
                  onProductLocate={handleProductLocate}
                  searchQuery={toLocateSearch}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Desktop layout */}
        <div className="hidden lg:flex flex-1 min-h-0">
          <div className="flex flex-col flex-1 pr-8 min-h-0">
            <div className="flex-shrink-0">
              {productsToLocate.length === 0 && locatedProducts.length > 0 ? (
                <LocateFinishedMessage />
              ) : (
                <ScanLocationSection
                  onLocationScan={handleLocationScan}
                  onScanUnits={handleScanUnits}
                  onClearLocation={handleClearLocation}
                  currentLocation={currentLocation}
                  isValidating={isValidatingLocation}
                  productBeingLocated={productBeingLocated}
                  quantityToLocate={quantityToLocate}
                  onQuantityChange={setQuantityToLocate}
                  isLocating={isLocating}
                  cartCode={returnedData?.cart_code}
                  batchSelectionCandidates={batchSelectionCandidates}
                  selectedBatchLineId={selectedBatchLineId}
                  onBatchLineSelect={handleBatchLineSelect}
                  onConfirmLocate={(batchId) => {
                    if (productBeingLocated && currentLocation) {
                      handleProductLocate(
                        productBeingLocated.id,
                        currentLocation.id,
                        productBeingLocated.code,
                        batchId,
                      );
                    }
                  }}
                />
              )}
            </div>

            {locatedProducts.length > 0 && (
              <div className="flex-1 mt-6 min-h-0">
                <LocatedProductsSection
                  products={filteredLocatedProducts}
                  onSort={handleLocatedSort}
                  currentSort={locatedSortBy}
                  searchQuery={locatedSearch}
                  onSearchChange={setLocatedSearch}
                />
              </div>
            )}
          </div>

          <div className="w-px bg-border flex-shrink-0" />

          <div className="flex flex-col flex-1 pl-8 min-h-0">
            <div className="flex flex-col h-full">
              <ProductsToLocateHeader
                products={filteredProductsToLocate}
                currentSort={toLocateSortBy}
                onSort={handleToLocateSort}
                searchQuery={toLocateSearch}
                onSearchChange={setToLocateSearch}
              />

              <div className="flex-1 overflow-y-auto">
                {filteredProductsToLocate.length === 0 ? (
                  <div className="bg-muted/30 border border-border/50 rounded-lg p-6">
                    <div className="flex items-center justify-center space-x-2 text-orange-600 dark:text-orange-400">
                      <span className="text-lg font-semibold">¡Todos los productos han sido ubicados!</span>
                    </div>
                  </div>
                ) : (
                  <ProductsToLocateSection
                    products={filteredProductsToLocate}
                    onProductLocate={handleProductLocate}
                    searchQuery={toLocateSearch}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
